import { type ApiError, type ApiSuccessEmpty } from '@lightdash/common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { lightdashApi } from '../api';
import { useUiStrings } from '../ee/providers/Embed/useUiStrings';
import { getAiAccessRefusal } from '../features/aiAccess/errors';
import useHealth from './health/useHealth';
import useToaster from './toaster/useToaster';

// TODO: This is a stub for the actual implementation
//       It could maybe be abstracted into a generic oauth login hook
const triggerSnowflakeLogin = async (
    siteUrl: string,
    loginPath: '/login/snowflake' | '/login/snowflake-ai',
) => {
    return new Promise<void>((resolve, reject) => {
        const channel = new BroadcastChannel('lightdash-oauth-popup');
        const loginUrl = `${siteUrl}/api/v1${loginPath}?isPopup=true`;
        console.info(`Opening popup with url: ${loginUrl}`);

        const popupWindow = window.open(
            loginUrl,
            'login-popup',
            'width=600,height=600',
        );

        if (!popupWindow) {
            reject(new Error('Failed to open popup window'));
            return;
        }

        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== siteUrl) return;

            if (event.data === 'success') {
                resolve();
            } else {
                reject(new Error('Authentication failed'));
            }

            channel.removeEventListener('message', handleMessage);
            channel.close();
            popupWindow.close();
        };

        channel.addEventListener('message', handleMessage);
    });
};

export const useIsSnowflakeSsoEnabled = () => {
    const health = useHealth();
    return !!health.data?.auth.snowflake.enabled;
};

export function useSnowflakeLoginPopup({
    onLogin: _onLogin,
}: {
    onLogin: () => Promise<void>;
}) {
    const { showToastError } = useToaster();
    const health = useHealth();
    const queryClient = useQueryClient();
    const ssoMutation = useMutation({
        mutationFn: () =>
            triggerSnowflakeLogin(
                health.data?.siteUrl || '',
                '/login/snowflake',
            ),
        onSuccess: async () => {
            // Invalidate user warehouse credentials since the backend creates
            // credentials during the OAuth flow
            await queryClient.invalidateQueries(['user_warehouse_credentials']);
            await _onLogin?.();
        },
        onError: (error: Error) => {
            showToastError({
                title: 'Authentication failed',
                subtitle: error.message || 'Please try again',
            });
        },
    });

    return useMemo(() => {
        return {
            ...ssoMutation,
            isSsoEnabled: health.data?.auth.snowflake.enabled,
        };
    }, [ssoMutation, health.data?.auth.snowflake.enabled]);
}

export function useSnowflakeAiLoginPopup() {
    const t = useUiStrings();
    const health = useHealth();
    const queryClient = useQueryClient();
    const { showToastError } = useToaster();
    return useMutation({
        mutationFn: () =>
            triggerSnowflakeLogin(
                health.data?.siteUrl || '',
                '/login/snowflake-ai',
            ),
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries(['user_warehouse_credentials']),
                queryClient.invalidateQueries(['ai-access']),
                queryClient.invalidateQueries({
                    predicate: (query) =>
                        !!getAiAccessRefusal(
                            (query.state.error as ApiError | null)?.error,
                        ),
                }),
            ]);
        },
        onError: (error: Error) =>
            showToastError({
                title: t('aiAccess.signInError'),
                subtitle: error.message,
            }),
    });
}

const getIsAuthenticated = async () =>
    lightdashApi<ApiSuccessEmpty['results']>({
        url: `/snowflake/sso/is-authenticated`,
        method: 'GET',
        body: undefined,
    });

export const useIsSnowflakeAuthenticated = () => {
    const health = useHealth();

    return useQuery<ApiSuccessEmpty['results'], ApiError>({
        queryKey: ['snowflake-sso-is-authenticated'],
        queryFn: getIsAuthenticated,
        enabled: health.data?.auth.snowflake.enabled === true,
        retry: false,
    });
};

export function useSnowflakeDatasets() {
    return { refetch: () => {} };
}
